import { useCallback } from 'react';
import { parseIdleTimeoutConfig } from '../config/idleTimeout';
import { useAuth } from '../contexts/auth-context';
import { useEncryption } from '../contexts/EncryptionContext';
import { useIdleTimeout } from '../hooks/useIdleTimeout';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

// Resolved once at module load; src/config/idleTimeout.ts stays free of
// import.meta so the backend tsconfig (CommonJS) can still compile it.
const IDLE_TIMEOUT_CONFIG = parseIdleTimeoutConfig(
  import.meta.env as Record<string, string | undefined>,
);

interface SessionIdleGuardProps {
  /** Arm the idle timer only on authenticated app surfaces. */
  active: boolean;
}

/**
 * SessionIdleGuard
 *
 * HIPAA §164.312(a)(2)(iii) automatic logoff. When the user has been idle
 * for the configured period, shows a "stay signed in" warning; if the
 * warning expires, drops all decrypted encryption key material from browser
 * memory and then signs the user out via Clerk.
 */
export function SessionIdleGuard({ active }: SessionIdleGuardProps) {
  const { signOut } = useAuth();
  const { clearKeyMaterial } = useEncryption();

  const handleTimeout = useCallback(() => {
    // Key material must be gone before the (async) sign-out redirect, so
    // decrypted keys never outlive the idle deadline.
    clearKeyMaterial();
    void signOut();
  }, [clearKeyMaterial, signOut]);

  const { isWarning, remainingSeconds, staySignedIn } = useIdleTimeout({
    enabled: active && IDLE_TIMEOUT_CONFIG.enabled,
    timeoutMs: IDLE_TIMEOUT_CONFIG.timeoutMs,
    warningMs: IDLE_TIMEOUT_CONFIG.warningMs,
    onTimeout: handleTimeout,
  });

  if (!isWarning) {
    return null;
  }

  return (
    <AlertDialog open>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Are you still there?</AlertDialogTitle>
          <AlertDialogDescription>
            For your security, you will be signed out in {remainingSeconds}{' '}
            {remainingSeconds === 1 ? 'second' : 'seconds'} due to inactivity.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleTimeout}>Sign out now</AlertDialogCancel>
          <AlertDialogAction onClick={staySignedIn}>Stay signed in</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
